import React from 'react';

/**
 * BrandedSplash — the <Suspense> fallback shown while a lazily-loaded route
 * chunk is fetched/parsed (see src/Routes.jsx). It MUST never read as a blank
 * white screen: it paints the hero's dark navy (#0a1628, the same
 * `--dark-bg` used by index.html's inline early-paint style and the mobile
 * homepage wrapper) with a centered, gently-pulsing brand mark so a route
 * transition on a slow connection feels intentional rather than broken.
 *
 * Deliberately dependency-free (no framer-motion / tailwind @layer classes)
 * and styled inline so it is tiny, lives in the eager entry chunk, and can
 * render before any route CSS/JS has loaded. The keyframes are injected once.
 */

const PULSE_KEYFRAMES = `
@keyframes obq-splash-pulse {
  0%, 100% { opacity: 0.55; transform: scale(0.97); }
  50%      { opacity: 1;    transform: scale(1.03); }
}
@keyframes obq-splash-bar {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}`;

function BrandedSplash() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        backgroundColor: '#0a1628',
        // paint into iOS safe-area zones, matching the hero
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <style>{PULSE_KEYFRAMES}</style>

      <img
        src="/assets/images/Openbooq_blue-1773069231613.png"
        alt="OpenbooQ"
        width={160}
        height={48}
        decoding="async"
        fetchpriority="high"
        style={{
          height: 'clamp(40px, 9vw, 64px)',
          width: 'auto',
          objectFit: 'contain',
          animation: 'obq-splash-pulse 1.4s ease-in-out infinite',
          filter: 'drop-shadow(0 6px 24px rgba(7,176,233,0.35))',
        }}
      />

      {/* Indeterminate progress sliver in the brand cyan */}
      <div
        aria-hidden="true"
        style={{
          position: 'relative',
          width: 'min(180px, 46vw)',
          height: 3,
          borderRadius: 999,
          overflow: 'hidden',
          backgroundColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '25%',
            borderRadius: 999,
            backgroundColor: '#07b0e9',
            animation: 'obq-splash-bar 1.1s ease-in-out infinite',
          }}
        />
      </div>

      <span
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Loading OpenbooQ
      </span>
    </div>
  );
}

export default BrandedSplash;
